import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Button
} from '@mui/material';
import { PricingTier } from '../types';
import { pricingTiers } from '../services/paymentService';
import { AccountBalanceWallet as WalletIcon, VerifiedUser as VerifiedUserIcon } from '@mui/icons-material';

interface PricingInfoProps {
  currentTier: PricingTier;
  nftCount: number;
  isWhitelisted?: boolean;
  walletAddress?: string | null;
  onConnectWallet?: () => void;
}

const PricingInfo: React.FC<PricingInfoProps> = ({ 
  currentTier, 
  nftCount, 
  isWhitelisted = false,
  walletAddress = null,
  onConnectWallet
}) => {
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Pricing Information
      </Typography>
      
      {!walletAddress && onConnectWallet && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" paragraph>
            Connect your wallet to check if you're eligible for free generation.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<WalletIcon />}
            onClick={onConnectWallet}
            size="small"
          >
            Connect Wallet
          </Button>
        </Box>
      )}
      
      {isWhitelisted && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Box display="flex" alignItems="center">
            <VerifiedUserIcon sx={{ mr: 1, color: 'success.dark' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
              Whitelisted Wallet
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your wallet is whitelisted! You can generate and download collections of any size for free.
          </Typography>
        </Box>
      )}
      
      {walletAddress && !isWhitelisted && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Box display="flex" alignItems="center">
            <WalletIcon sx={{ mr: 1, color: 'info.dark' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
              Standard Wallet
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your wallet is not whitelisted. Generation fees depend on the number of NFTs in your collection according to the pricing tiers below.
          </Typography>
          {currentTier.price !== "0" && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              Current rate for {nftCount} NFTs: {currentTier.price} ETH
            </Typography>
          )}
        </Box>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" paragraph>
          Your current plan: <Chip label={currentTier.label} color="primary" size="small" /> for {nftCount} NFTs
        </Typography>
        
        {isWhitelisted ? (
          <Typography variant="body2" color="success.main" fontWeight="bold">
            Free tier (Whitelisted) - No payment required
          </Typography>
        ) : currentTier.price === "0" ? (
          <Typography variant="body2" color="success.main" fontWeight="bold">
            Free tier - No payment required
          </Typography>
        ) : (
          <Typography variant="body2">
            Price: <Typography component="span" fontWeight="bold">{currentTier.price} ETH</Typography>
          </Typography>
        )}
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2" gutterBottom>
        All Pricing Tiers
      </Typography>
      
      <Grid container spacing={2}>
        {pricingTiers.map((tier) => (
          <Grid item xs={12} sm={6} md={4} key={tier.label}>
            <Box 
              sx={{ 
                p: 2, 
                border: '1px solid', 
                borderColor: tier.label === currentTier.label ? 'primary.main' : 'divider',
                borderRadius: 1,
                bgcolor: tier.label === currentTier.label ? 'rgba(98, 0, 234, 0.05)' : 'transparent'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {tier.label}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {tier.minNFTs} - {tier.maxNFTs === null ? '∞' : tier.maxNFTs} NFTs
              </Typography>
              
              <Typography variant="h6" color="primary">
                {tier.price === "0" ? "Free" : `${tier.price} ETH`}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Payments are processed on the Base network. You'll need ETH to complete your purchase.
        </Typography>
      </Box>
    </Paper>
  );
};

export default PricingInfo; 